import React, { useState, useEffect } from 'react';
import { Key, CheckCircle, AlertCircle, ExternalLink, TestTube, Server, HardDrive } from 'lucide-react';
import { testTMDBConnection, testHorrorMovie } from '../../utils/tmdbTest';
import { getTMDBApiKey } from '../../services/settingsService';
import { isSupabaseConfigured } from '../../config/supabase';
import { useEmailAuth } from '../../hooks/useEmailAuth';
import { saveTMDBApiKeyAppLevel, saveTMDBBearerTokenAppLevel } from '../../services/appLevelSettings';

export default function TMDBSettings() {
  const { isUserAuthenticated } = useEmailAuth();
  const [apiKey, setApiKey] = useState('');
  const [bearerToken, setBearerToken] = useState('');
  const [testStatus, setTestStatus] = useState(null); // null, 'testing', 'success', 'error'
  const [error, setError] = useState('');
  const [serverStatus, setServerStatus] = useState(null); // null, 'saving', 'success', 'error'
  const [serverError, setServerError] = useState('');
  const [hasServerKey, setHasServerKey] = useState(false);
  const [, setHasServerBearerToken] = useState(false);

  useEffect(() => {
    // Load saved API key from localStorage
    const savedKey = localStorage.getItem('tmdb_api_key');
    if (savedKey) {
      setApiKey(savedKey);
    }
    
    // Load saved Bearer token from localStorage
    const savedBearerToken = localStorage.getItem('tmdb_bearer_token');
    if (savedBearerToken) {
      setBearerToken(savedBearerToken);
    }
    
    // Check if server has API key
    checkServerKey();
  }, []);

  const checkServerKey = async () => {
    if (!isSupabaseConfigured()) return;
    
    try {
      const { data: serverKey } = await getTMDBApiKey();
      setHasServerKey(!!serverKey);
    } catch (err) {
      console.warn('Could not check server TMDB key:', err);
    }
  };


  const saveApiKey = () => {
    localStorage.setItem('tmdb_api_key', apiKey);
    // Also set as environment variable for this session
    window.REACT_APP_TMDB_API_KEY = apiKey;
    testApiKey();
  };

  const saveBearerToken = () => {
    localStorage.setItem('tmdb_bearer_token', bearerToken);
    // Also set as environment variable for this session
    window.REACT_APP_TMDB_BEARER_TOKEN = bearerToken;
  };



  const saveApiKeyToServer = async () => {
    if (!apiKey.trim()) {
      setServerError('Please enter an API key');
      return;
    }

    if (!isSupabaseConfigured()) {
      setServerError('Supabase is not configured. Please configure Supabase first.');
      return;
    }

    setServerStatus('saving');
    setServerError('');

    try {
      const { error } = await saveTMDBApiKeyAppLevel(apiKey.trim());
      if (error) {
        throw error;
      }

      setServerStatus('success');
      setHasServerKey(true);
      setTimeout(() => setServerStatus(null), 3000);
    } catch (err) {
      setServerStatus('error');
      setServerError(`Failed to save: ${err.message}`);
      console.error('Error saving TMDB API key:', err);
    }
  };

  const saveBearerTokenToServer = async () => {
    if (!bearerToken.trim()) {
      setServerError('Please enter a Bearer token');
      return;
    }

    if (!isSupabaseConfigured()) {
      setServerError('Supabase is not configured. Please configure Supabase first.');
      return;
    }

    setServerStatus('saving');
    setServerError('');

    try {
      const { error } = await saveTMDBBearerTokenAppLevel(bearerToken.trim());
      if (error) {
        throw error;
      }

      setServerStatus('success');
      setHasServerBearerToken(true);
      setTimeout(() => setServerStatus(null), 3000);
    } catch (err) {
      setServerStatus('error');
      setServerError(`Failed to save Bearer token: ${err.message}`);
      console.error('Error saving TMDB Bearer token:', err);
    }
  };

  const testApiKey = async () => {
    if (!apiKey.trim()) {
      setError('Please enter an API key');
      return;
    }

    setTestStatus('testing');
    setError('');

    try {
      const response = await fetch(
        `https://api.themoviedb.org/3/configuration?api_key=${apiKey.trim()}`
      );

      if (response.ok) {
        setTestStatus('success');
        setTimeout(() => setTestStatus(null), 3000);
      } else {
        throw new Error(`API returned ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      setTestStatus('error');
      setError(err.message);
    }
  };

  const clearApiKey = () => {
    setApiKey('');
    localStorage.removeItem('tmdb_api_key');
    delete window.REACT_APP_TMDB_API_KEY;
    setTestStatus(null);
    setError('');
  };

  return (
    <div className="bg-gray-800 border-2 border-orange-600 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-4">
        <Key className="text-orange-400" size={24} />
        <h2 className="text-xl font-bold text-orange-300">TMDB API Configuration</h2>
      </div>

      <div className="space-y-4">
        <div>
          <p className="text-orange-200 text-sm mb-3">
            Enter your TMDB API key to enable rich movie data including posters, trailers, 
            cast information, and streaming providers.
          </p>
          
          <div className="flex items-center gap-2 mb-2">
            <span className="text-orange-300 text-sm">Don't have an API key?</span>
            <a
              href="https://www.themoviedb.org/settings/api"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
            >
              Get one free from TMDB <ExternalLink size={12} />
            </a>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-orange-300 text-sm font-medium mb-2">
              API Key
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Enter your TMDB API key..."
              className="w-full bg-gray-700 border border-orange-500 rounded-md px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-400"
            />
          </div>

          <div>
            <label className="block text-orange-300 text-sm font-medium mb-2">
              Bearer Token (Read Access Token)
            </label>
            <input
              type="password"
              value={bearerToken}
              onChange={(e) => setBearerToken(e.target.value)}
              placeholder="Enter your TMDB Bearer token (for list access)..."
              className="w-full bg-gray-700 border border-orange-500 rounded-md px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-400"
            />
            <p className="text-orange-200 text-xs mt-1">
              Required for importing TMDB lists. Get from your TMDB account settings → API → Read Access Token.
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={saveApiKey}
              disabled={!apiKey.trim() || testStatus === 'testing'}
              className="bg-orange-600 hover:bg-orange-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md transition-colors flex items-center gap-2"
            >
              {testStatus === 'testing' ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Testing...
                </>
              ) : (
                <>
                  <CheckCircle size={16} />
                  Save & Test
                </>
              )}
            </button>

            {apiKey && (
              <button
                onClick={clearApiKey}
                className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-md transition-colors"
              >
                Clear
              </button>
            )}

            <button
              onClick={saveBearerToken}
              disabled={!bearerToken.trim()}
              className="bg-purple-600 hover:bg-purple-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md transition-colors flex items-center gap-2"
            >
              <Key size={16} />
              Save Bearer Token
            </button>

            {bearerToken && (
              <button
                onClick={() => {
                  setBearerToken('');
                  localStorage.removeItem('tmdb_bearer_token');
                  delete window.REACT_APP_TMDB_BEARER_TOKEN;
                }}
                className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-md transition-colors"
              >
                Clear Bearer
              </button>
            )}

            <button
              onClick={async () => {
                console.log('🎬 Running TMDB integration test...');
                await testTMDBConnection();
                await testHorrorMovie();
                console.log('✅ Check the browser console for detailed test results!');
              }}
              className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-md transition-colors flex items-center gap-2"
            >
              <TestTube size={16} />
              Test Integration
            </button>
          </div>

          {/* Server-side Storage (Admin Only) */}
          {isUserAuthenticated && isSupabaseConfigured() && (
            <div className="border-t border-gray-600 pt-4 mt-4">
              <h3 className="text-orange-300 font-semibold mb-2 flex items-center gap-2">
                <Server size={18} />
                Server-side Storage
              </h3>
              <p className="text-orange-200 text-sm mb-3">
                Store the API key on the server so all users can see enhanced movie data without needing their own key.
              </p>
              
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={saveApiKeyToServer}
                  disabled={!apiKey.trim() || serverStatus === 'saving'}
                  className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md transition-colors flex items-center gap-2"
                >
                  {serverStatus === 'saving' ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Saving to Server...
                    </>
                  ) : (
                    <>
                      <HardDrive size={16} />
                      Save to Server
                    </>
                  )}
                </button>

                <button
                  onClick={saveBearerTokenToServer}
                  disabled={!bearerToken.trim() || serverStatus === 'saving'}
                  className="bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md transition-colors flex items-center gap-2"
                >
                  {serverStatus === 'saving' ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Saving Bearer Token...
                    </>
                  ) : (
                    <>
                      <Server size={16} />
                      Save Bearer to Server
                    </>
                  )}
                </button>
                
                <button
                  onClick={async () => {
                    if (window.confirm('This will clear your authentication state and require you to sign in again. Continue?')) {
                      // Clear all auth-related storage
                      const keysToRemove = [];
                      for (let i = 0; i < localStorage.length; i++) {
                        const key = localStorage.key(i);
                        if (key && (key.includes('supabase') || key.includes('sb-') || key.includes('auth'))) {
                          keysToRemove.push(key);
                        }
                      }
                      keysToRemove.forEach(key => localStorage.removeItem(key));
                      
                      alert('Authentication state cleared. Please refresh the page and sign in again.');
                    }
                  }}
                  className="bg-red-600 hover:bg-red-500 text-white px-3 py-2 rounded-md transition-colors flex items-center gap-2 text-sm"
                  title="Clear stale authentication data"
                >
                  <AlertCircle size={14} />
                  Clear Auth
                </button>
                
                {hasServerKey && (
                  <div className="flex items-center gap-2 text-green-400 text-sm">
                    <CheckCircle size={16} />
                    API key stored on server
                  </div>
                )}
              </div>
              
              {/* Server Status Messages */}
              {serverStatus === 'success' && (
                <div className="flex items-center gap-2 text-green-400 text-sm mt-2">
                  <CheckCircle size={16} />
                  API key successfully saved to server! All users can now see enhanced movie data.
                </div>
              )}
              
              {serverStatus === 'error' && serverError && (
                <div className="flex items-center gap-2 text-red-400 text-sm mt-2">
                  <AlertCircle size={16} />
                  {serverError}
                </div>
              )}
            </div>
          )}

          {/* Status Messages */}
          {testStatus === 'success' && (
            <div className="flex items-center gap-2 text-green-400 text-sm">
              <CheckCircle size={16} />
              API key is valid and working!
            </div>
          )}

          {testStatus === 'error' && (
            <div className="flex items-center gap-2 text-red-400 text-sm">
              <AlertCircle size={16} />
              Error: {error}
            </div>
          )}

          {error && testStatus !== 'error' && (
            <div className="text-red-400 text-sm">
              {error}
            </div>
          )}
        </div>

        <div className="bg-gray-700 rounded-md p-3">
          <h3 className="text-orange-300 text-sm font-medium mb-2">What you'll get:</h3>
          <ul className="text-orange-200 text-sm space-y-1">
            <li>• High-quality movie posters</li>
            <li>• Official trailers and videos</li>
            <li>• Detailed plot summaries</li>
            <li>• Cast and crew information</li>
            <li>• Streaming availability (US)</li>
            <li>• Accurate release dates and runtimes</li>
            <li>• User ratings and reviews</li>
          </ul>
        </div>

        <div className="bg-yellow-900 border border-yellow-600 rounded-md p-3">
          <div className="flex items-center gap-2 text-yellow-300 text-sm">
            <AlertCircle size={16} />
            <strong>Note:</strong> Your API key is stored locally in your browser and never sent to our servers.
          </div>
        </div>
      </div>
    </div>
  );
}
